"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {useTextScriptNames} from "@/hooks/useTextScriptNames";
import {snapshotValuesEqual, type MutationProposal} from "@/services/wingman/mutation-proposal";
import FrozenValue from "./FrozenValue";
import ProposalFieldLabel from "./ProposalFieldLabel";
import UseCaseStepsDiff from "./UseCaseStepsDiff";
import {TextDiffSide} from "./ProposalDiff";
import UseCaseStepList from "./UseCaseStepList";
import {CHANGE_BADGE, actionFieldLabel, fieldChange, referencesTextScript} from "./useCaseSnapshot";
import {occurrenceKeys} from "./snapshotKeys";

/** The receipt already proved the saved ID matches the proposal's target, so it is not worth showing. */
const withoutId = ({id: _id, ...fields}: Record<string, unknown>) => fields;

interface UseCaseSnapshotDiffProps {
   preview: MutationProposal;
   /** The Action as Galactus saved it, once execution is verified. */
   saved?: Record<string, unknown> | null;
   /** Show what was saved without a click, where it is the main thing left to see. */
   savedOpen?: boolean;
}

function SnapshotFieldDiff({preview, field, scriptNames}: {
   preview: MutationProposal;
   field: string;
   scriptNames: ReadonlyMap<string, string>;
}) {
   const {t} = useTranslation("ask");
   if (field === "actions" && Array.isArray(preview.before.actions) && Array.isArray(preview.after.actions)) return <UseCaseStepsDiff before={preview.before.actions} after={preview.after.actions} scriptNames={scriptNames} />;
   if (typeof preview.before[field] === "string" && typeof preview.after[field] === "string") return <div className="grid gap-3 text-sm leading-6 text-800 @md:grid-cols-2">
      {(["before", "after"] as const).map(side => <div key={side} data-side={side} className="min-w-0 rounded-lg border border-300 bg-panel p-3">
         <p className="mb-2 text-xs font-semibold text-500">{t(`toolApproval.${side}`)}</p>
         <TextDiffSide before={preview.before[field] as string} after={preview.after[field] as string} side={side} />
      </div>)}
   </div>;
   return <div className="grid grid-cols-1 items-start gap-3 @md:grid-cols-2">
      {(["before", "after"] as const).map(side => <div key={side} data-side={side} className="min-w-0 rounded-lg border border-300 bg-panel p-3 text-sm leading-6 text-800">
         <p className="mb-2 text-xs font-semibold text-500">{t(`toolApproval.${side}`)}</p>
         <FrozenValue value={preview[side][field]} field={field} scriptNames={scriptNames} />
      </div>)}
   </div>;
}

/** Preserve both complete ordered snapshots while identifying the changes to each step. */
export default function UseCaseSnapshotDiff({preview, saved, savedOpen}: UseCaseSnapshotDiffProps) {
   const {t} = useTranslation("ask");
   const scriptNames = useTextScriptNames(referencesTextScript(preview.before, preview.after, saved));
   // Match the editor's reading order, regardless of the API's object key order.
   const fields = [...new Set(["title", "description", ...Object.keys(preview.before), ...Object.keys(preview.after)])];
   const changedFields = fields.filter(field => !snapshotValuesEqual(preview.before[field], preview.after[field]) || Object.hasOwn(preview.before, field) !== Object.hasOwn(preview.after, field));
   return <div className="@container divide-y divide-neutral">
      {occurrenceKeys(preview.warnings, warning => warning).map(({key, item: warning}) => <p key={key} role="note" className="m-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">{warning}</p>)}
      {!changedFields.length && <p className="p-4 text-sm text-500">{t("toolApproval.noChanges")}</p>}
      {changedFields.map(field => {
         const change = fieldChange(preview.before, preview.after, field);
         return <section key={field} className="p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-800">
               <ProposalFieldLabel field={actionFieldLabel(field)} />
               {change !== "edited" && <span data-change={change} className={clsx("rounded px-2 py-0.5 text-xs", CHANGE_BADGE[change])}>{t(`toolApproval.${change}`)}</span>}
            </h3>
            <SnapshotFieldDiff preview={preview} field={field} scriptNames={scriptNames} />
         </section>;
      })}
      <details className="p-4 text-sm text-700">
         <summary className="cursor-pointer font-medium">{t("toolApproval.fullSnapshots")}</summary>
         <div className="mt-3 grid grid-cols-1 gap-3 @md:grid-cols-2">
            {(["before", "after"] as const).map(side => <div key={side} className="min-w-0 rounded-lg border border-300 bg-panel p-3">
               <h3 className="mb-3 text-xs font-semibold text-500">{t(`toolApproval.${side}`)}</h3>
               <FrozenValue value={preview[side]} scriptNames={scriptNames} />
            </div>)}
         </div>
      </details>
      {saved && !snapshotValuesEqual(saved, preview.after) && <details open={savedOpen} className="p-4 text-sm text-700">
         <summary className="cursor-pointer font-medium">{t("toolApproval.savedVersion")}</summary>
         <div className="mt-3 min-w-0 rounded-lg border border-300 bg-panel p-3">
            <dl className="space-y-3">{Object.entries(withoutId(saved)).map(([field, value]) => <div key={field}>
               <dt className="mb-1 text-xs font-semibold text-500"><ProposalFieldLabel field={actionFieldLabel(field)} /></dt>
               <dd>{field === "actions" && Array.isArray(value)
                  ? <UseCaseStepList items={value} changes={value.map(() => ({kind: "unchanged"}))} scriptNames={scriptNames} />
                  : <FrozenValue value={value} field={field} scriptNames={scriptNames} />}</dd>
            </div>)}</dl>
         </div>
      </details>}
   </div>;
}
